/**
 * The Lineup — color swatch variant switching
 *
 * Clicking a swatch switches the card to that variant: image, price,
 * compare-at price, every link (image / title / CTA) and the Affirm estimate.
 * Mirrors the data-attribute contract used by collection-products.js.
 */
(function () {
  'use strict';

  var CARD = '[data-the-lineup-card]';
  var SWATCH = '.tl-card__swatch';

  /**
   * Convert a money string ("$1,999" / "$1,999.00") to integer cents.
   * @param {string} money
   * @returns {number|null}
   */
  function moneyToCents(money) {
    if (!money) return null;
    var raw = String(money).replace(/[^0-9.]/g, '');
    if (!raw) return null;
    var amount = parseFloat(raw);
    if (isNaN(amount)) return null;
    return Math.round(amount * 100);
  }

  /**
   * Apply a swatch's variant data to its card.
   * @param {HTMLElement} swatch
   */
  function selectSwatch(swatch) {
    var card = swatch.closest(CARD);
    if (!card) return;

    var variantId = swatch.getAttribute('data-variant-id');
    if (!variantId) return;

    // --- active state ---
    var all = card.querySelectorAll(SWATCH);
    for (var i = 0; i < all.length; i++) {
      all[i].classList.remove('is-active');
      all[i].setAttribute('aria-pressed', 'false');
    }
    swatch.classList.add('is-active');
    swatch.setAttribute('aria-pressed', 'true');
    card.setAttribute('data-current-variant', variantId);

    // --- image ---
    var image = swatch.getAttribute('data-variant-image');
    var img = card.querySelector('[data-tl-image]');
    if (image && img) {
      img.setAttribute('src', image);
      // srcset would otherwise win over the new src
      img.setAttribute('srcset', image + ' 1200w');
      var alt = swatch.getAttribute('data-variant-image-alt');
      if (alt) img.setAttribute('alt', alt);
    }

    // --- price ---
    var price = swatch.getAttribute('data-variant-price');
    var priceEl = card.querySelector('[data-tl-price]');
    if (price && priceEl) priceEl.textContent = price;

    // --- compare-at price (hidden when this variant isn't on sale) ---
    var compare = swatch.getAttribute('data-variant-compare-price');
    var compareEl = card.querySelector('[data-tl-compare-price]');
    if (compareEl) {
      if (compare) {
        compareEl.textContent = compare;
        compareEl.hidden = false;
      } else {
        compareEl.textContent = '';
        compareEl.hidden = true;
      }
    }

    // --- links follow the variant ---
    var url = swatch.getAttribute('data-variant-url');
    if (url) {
      var links = card.querySelectorAll('[data-tl-link]');
      for (var j = 0; j < links.length; j++) {
        links[j].setAttribute('href', url);
      }
    }

    // --- Affirm estimate ---
    // affirm.ui.refresh() is GLOBAL: it rescans the document and re-renders
    // every .affirm-as-low-as on the page, which made clicking one card's
    // swatch visibly refresh the other cards too. Only call it when this
    // card's amount actually changed, and pin every other card's amount to
    // what it already shows so the global rescan cannot alter them.
    var affirm = card.querySelector('[data-tl-affirm]');
    if (affirm && price) {
      var cents = moneyToCents(price);
      if (cents !== null && affirm.getAttribute('data-amount') !== String(cents)) {
        affirm.setAttribute('data-amount', String(cents));

        if (window.affirm && window.affirm.ui && window.affirm.ui.refresh) {
          // Every other Affirm element keeps its current data-amount, so the
          // rescan re-renders them with the values they already had.
          window.affirm.ui.refresh();
        }
      }
    }

    card.dispatchEvent(new CustomEvent('the-lineup:variant-change', {
      bubbles: true,
      detail: { variantId: variantId, color: swatch.getAttribute('data-color-value') }
    }));
  }

  /**
   * One delegated listener handles every card, including sections the
   * theme editor re-renders after this script has already run.
   * @param {Event} event
   */
  function onClick(event) {
    var target = event.target;
    if (!(target instanceof Element)) return;
    var swatch = target.closest(SWATCH);
    if (!swatch || !swatch.closest(CARD)) return;
    // Manual-color swatches carry no variant data — nothing to switch to.
    if (!swatch.getAttribute('data-variant-id')) return;
    event.preventDefault();
    event.stopPropagation();
    selectSwatch(/** @type {HTMLElement} */ (swatch));
  }

  document.addEventListener('click', onClick);

  // Keyboard: buttons fire click on Enter/Space natively, so nothing extra needed.
})();
